import React from 'react';
import {
    AtSign,
    Cable,
    Check,
    Globe,
    LucideIcon,
    Mail,
    Palette,
    ScanEye,
    ShoppingBag,
    Swords,
    Target,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Connection, ConnectionKey } from '../../../../common/hooks/use-home-api.hook';

/**
 * What is connected, and what is left — on Home.
 *
 * The team can only work with what it can see: no website means no brand to
 * read, no shop means no revenue, no social account means nowhere to post. So
 * Home says plainly which of these are in place and puts the way to connect
 * the rest one click away. The states come from the API, read live from each
 * service; this only draws them.
 */

export const CONNECTION_ICONS: Record<ConnectionKey, LucideIcon> = {
    website: Globe,
    visualizer: ScanEye,
    shop: ShoppingBag,
    social: AtSign,
    ads: Target,
    email: Mail,
    brand: Palette,
    competitors: Swords,
    channels: Cable,
};

/** Where each is connected — the same pages the setup cards send a vendor to. */
export const CONNECTION_ROUTES: Record<ConnectionKey, string> = {
    website: '/marketing/brand',
    visualizer: '/home/integration',
    shop: '/business/sales/shop',
    social: '/marketing/social-scheduler?tab=accounts',
    ads: '/ads/connections',
    email: '/marketing/email/settings?tab=smtp',
    brand: '/marketing/brand',
    competitors: '/business/competitors/rivals',
    channels: '/home/channels',
};

const keyOf = (key: string) => key.toUpperCase();

export const Connections: React.FC<{
    connections: Connection[] | null;
    /** `list` is the side panel's: one column, no heading of its own. */
    layout?: 'section' | 'list';
}> = ({ connections, layout = 'section' }) => {
    const list = layout === 'list';
    const { t } = useTranslation();
    const navigate = useNavigate();

    const countable = (connections ?? []).filter((c) => c.state !== 'locked');
    const done = countable.filter((c) => c.state === 'connected').length;

    /** The line under the name: what is connected, or what connecting would give. */
    const line = (c: Connection): string => {
        const k = keyOf(c.key);
        if (c.state === 'locked') return t('AGENTS.CONNECT.LOCKED', 'Not in your plan');
        if (c.state === 'missing') return t(`AGENTS.CONNECT.ITEM.${k}.HINT`);
        if (c.key === 'visualizer') return t('AGENTS.CONNECT.VIEWS', { count: c.count ?? 0, defaultValue: '{{count}} views in 30 days' });
        if (c.key === 'brand') return t('AGENTS.CONNECT.BRAND_READ', 'Read from your website');
        if (c.key === 'channels' && c.state === 'attention' && !c.detail) return t('AGENTS.CONNECT.CHANNELS_PAIR', 'Set up — link a chat to finish');
        if (c.key === 'competitors' && c.state === 'attention') {
            return t('AGENTS.CONNECT.ONE_MORE', { detail: c.detail, defaultValue: '{{detail}} — add one more to compare' });
        }
        if (c.state === 'attention') return t('AGENTS.CONNECT.RECONNECT_HINT', { detail: c.detail, defaultValue: '{{detail}} — needs reconnecting' });
        return c.detail ?? t('AGENTS.CONNECT.STATE.CONNECTED', 'Connected');
    };

    return (
        <section className={cn(!list && 'mx-auto w-full max-w-5xl px-4 pb-10')}>
            <div className="mb-3 flex items-baseline justify-between gap-3">
                {!list && <h2 className="text-sm font-medium">{t('AGENTS.CONNECT.TITLE', 'Connections')}</h2>}
                {connections && (
                    <span className="text-xs text-muted-foreground">
                        {t('AGENTS.CONNECT.SUMMARY', { done, total: countable.length, defaultValue: '{{done}} of {{total}} connected' })}
                    </span>
                )}
            </div>

            <div className={cn('grid grid-cols-1 gap-2', !list && 'sm:grid-cols-2 lg:grid-cols-4')}>
                {!connections && [0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="h-[68px] rounded-xl" />)}

                {connections?.map((c) => {
                    const Icon = CONNECTION_ICONS[c.key];
                    const route = c.state === 'locked' ? '/home/billing/upgrade' : CONNECTION_ROUTES[c.key];
                    const connected = c.state === 'connected';

                    // One control per tile: the whole tile is the button, and
                    // "Connect" is drawn as one rather than nested inside it.
                    return (
                        <button
                            key={c.key}
                            type="button"
                            onClick={() => navigate(route)}
                            className={cn(
                                'flex items-center gap-3 rounded-xl border bg-card p-3 text-start transition-colors hover:border-foreground/25',
                                c.state === 'locked' && 'opacity-60',
                            )}
                        >
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background">
                                <Icon className="size-4 text-muted-foreground" aria-hidden />
                            </span>

                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="flex items-center gap-1.5 text-sm font-medium">
                                    <span className="truncate">{t(`AGENTS.CONNECT.ITEM.${keyOf(c.key)}.NAME`)}</span>
                                    {c.state === 'attention' && <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden />}
                                </span>
                                <span className="truncate text-xs text-muted-foreground">{line(c)}</span>
                            </span>

                            {connected ? (
                                <>
                                    <Check className="size-4 shrink-0 text-success" aria-hidden />
                                    <span className="sr-only">{t('AGENTS.CONNECT.STATE.CONNECTED', 'Connected')}</span>
                                </>
                            ) : (
                                <span className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'pointer-events-none shrink-0')}>
                                    {c.state === 'attention'
                                        ? t('AGENTS.CONNECT.ACTION.FIX', 'Fix')
                                        : c.state === 'locked'
                                            ? t('AGENTS.CONNECT.ACTION.UPGRADE', 'Upgrade')
                                            : t('AGENTS.CONNECT.ACTION.CONNECT', 'Connect')}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </section>
    );
};
